// src/components/resources/tools/generators/AiChat/renderMessageContent.tsx

import { Fragment, type ReactNode } from 'react'
import { cn } from '~/lib/utils'

/**
 * Theme + provider styling hooks for the Markdown-subset renderer. Each
 * provider passes its own palette so the same parser produces ChatGPT-,
 * Claude-, and Gemini-accurate code blocks, links, and list markers without
 * forking the parsing logic.
 */
export interface MessageContentStyles {
  /** Class applied to each paragraph. */
  paragraph: string
  /** Class applied to `## heading` lines. */
  heading: string
  /** Class applied to inline `` `code` `` spans. */
  inlineCode: string
  /** Class applied to the fenced code-block container. */
  codeBlock: string
  /** Class applied to the code-block language chip / header row. */
  codeBlockHeader: string
  /** Class applied to `- ` unordered list markers. */
  bulletMarker: string
  /** Class applied to `1. ` ordered list markers. */
  orderedMarker: string
  /** Class applied to `**bold**` runs. */
  strong: string
  /** Class applied to `[text](url)` links. */
  link: string
}

/**
 * Inline-token matcher for `` `code` ``, `**bold**`, and `[text](url)`. Hoisted
 * to module scope so it's compiled once rather than on every `renderInline`
 * call (this runs for every line of every assistant turn per render). `String
 * .split` ignores a global regex's `lastIndex`, so sharing one instance across
 * calls is safe. Order matters: code spans win over bold so a backtick inside a
 * response isn't mis-parsed as emphasis.
 */
const INLINE_TOKEN_PATTERN = /(`[^`]+`|\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g

/**
 * Parse the inline Markdown subset inside a single line into styled React
 * nodes. Deliberately tiny and allocation-light.
 */
function renderInline(text: string, styles: MessageContentStyles, keyPrefix: string): ReactNode[] {
  // Split on the inline tokens while keeping the delimiters so we can re-wrap
  // each captured run.
  const parts = text.split(INLINE_TOKEN_PATTERN).filter(Boolean)

  return parts.map((part, index) => {
    const key = `${keyPrefix}-${index}`

    // `> 2` (not `> 1`): a valid code span is at least `` `x` `` (length 3), so
    // a bare "``" segment renders as literal text rather than an empty <code/>.
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return (
        <code key={key} className={styles.inlineCode}>
          {part.slice(1, -1)}
        </code>
      )
    }

    if (part.startsWith('**') && part.endsWith('**') && part.length > 3) {
      return (
        <strong key={key} className={styles.strong}>
          {part.slice(2, -2)}
        </strong>
      )
    }

    const linkMatch = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part)
    if (linkMatch) {
      return (
        <span key={key} className={styles.link}>
          {linkMatch[1]}
        </span>
      )
    }

    return <Fragment key={key}>{part}</Fragment>
  })
}

interface Block {
  type: 'paragraph' | 'heading' | 'code' | 'ul' | 'ol'
  lines: string[]
  /** Fenced-code language hint, when present. */
  lang?: string
}

/**
 * Group raw content lines into block-level structures (paragraphs, headings,
 * fenced code, bullet / numbered lists). A blank line ends the current
 * paragraph; fenced ``` regions swallow everything up to the closing fence so
 * code is never re-parsed for inline tokens.
 */
function parseBlocks(content: string): Block[] {
  const lines = content.replace(/\r\n/g, '\n').split('\n')
  const blocks: Block[] = []
  let current: Block | null = null
  let inCode = false

  const flush = (): void => {
    if (current) blocks.push(current)
    current = null
  }

  for (const rawLine of lines) {
    const line = rawLine

    const fenceMatch = /^```(\w+)?\s*$/.exec(line.trim())
    if (fenceMatch) {
      if (inCode) {
        // Closing fence — commit the code block.
        flush()
        inCode = false
      } else {
        flush()
        inCode = true
        current = { type: 'code', lines: [], lang: fenceMatch[1] }
      }
      continue
    }

    if (inCode) {
      if (current) current.lines.push(line)
      continue
    }

    if (line.trim() === '') {
      flush()
      continue
    }

    if (/^#{1,3}\s+/.test(line)) {
      flush()
      blocks.push({ type: 'heading', lines: [line.replace(/^#{1,3}\s+/, '')] })
      continue
    }

    if (/^\s*[-*]\s+/.test(line)) {
      if (current?.type !== 'ul') {
        flush()
        current = { type: 'ul', lines: [] }
      }
      current.lines.push(line.replace(/^\s*[-*]\s+/, ''))
      continue
    }

    if (/^\s*\d+\.\s+/.test(line)) {
      if (current?.type !== 'ol') {
        flush()
        current = { type: 'ol', lines: [] }
      }
      current.lines.push(line.replace(/^\s*\d+\.\s+/, ''))
      continue
    }

    if (current?.type !== 'paragraph') {
      flush()
      current = { type: 'paragraph', lines: [] }
    }
    current.lines.push(line)
  }

  flush()
  return blocks
}

/**
 * Render a Markdown-subset string into provider-styled React nodes.
 *
 * Supports paragraphs (with soft line breaks), `##` headings, fenced code
 * blocks with an optional language chip, inline `` `code` ``, `**bold**`,
 * `[text](url)` links, and `- ` / `1. ` lists. Everything else renders as
 * literal text — the goal is believable assistant formatting, not a complete
 * CommonMark implementation.
 */
export function renderMessageContent(content: string, styles: MessageContentStyles): ReactNode {
  const blocks = parseBlocks(content)

  return blocks.map((block, blockIndex) => {
    const key = `block-${blockIndex}`

    switch (block.type) {
      case 'code':
        return (
          <div key={key} className={styles.codeBlock}>
            <div className={styles.codeBlockHeader}>{block.lang ?? 'code'}</div>
            <pre className="overflow-x-auto px-4 py-3 text-[13px] leading-[1.5]">
              <code>{block.lines.join('\n')}</code>
            </pre>
          </div>
        )

      case 'heading':
        return (
          <p key={key} className={styles.heading}>
            {renderInline(block.lines[0] ?? '', styles, key)}
          </p>
        )

      case 'ul':
        return (
          <ul key={key} className="my-2 space-y-1.5 pl-1">
            {block.lines.map((line, i) => (
              <li key={`${key}-${i}`} className="flex gap-2.5">
                <span className={cn('mt-[0.55em] h-[5px] w-[5px] shrink-0 rounded-full', styles.bulletMarker)} />
                <span className="flex-1">{renderInline(line, styles, `${key}-${i}`)}</span>
              </li>
            ))}
          </ul>
        )

      case 'ol':
        return (
          <ol key={key} className="my-2 space-y-1.5 pl-1">
            {block.lines.map((line, i) => (
              <li key={`${key}-${i}`} className="flex gap-2.5">
                <span className={cn('shrink-0 tabular-nums', styles.orderedMarker)}>{i + 1}.</span>
                <span className="flex-1">{renderInline(line, styles, `${key}-${i}`)}</span>
              </li>
            ))}
          </ol>
        )

      case 'paragraph':
      default:
        return (
          <p key={key} className={styles.paragraph}>
            {block.lines.map((line, i) => (
              <Fragment key={`${key}-${i}`}>
                {i > 0 ? <br /> : null}
                {renderInline(line, styles, `${key}-${i}`)}
              </Fragment>
            ))}
          </p>
        )
    }
  })
}
